import React, { useMemo, useCallback, useEffect } from 'react';
import { Background, Controls, MiniMap, ReactFlow, Node, Edge, MarkerType, Position, useNodesState, useEdgesState, Handle } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CommitmentDetail } from '@/api/commitment';
import { SupportedChainId } from '@packet/constants/AppSupportChains';
import { truncateAddress } from '@/utils/address';
import AssetAmount from '@/components/AssetView/AssetAmount';
import AssetSymbol from '@/components/AssetView/AssetSymbol';
import styled from 'styled-components';
import { Hash } from 'viem';

const FlowContainer = styled.div`
  width: 100%;
  height: 600px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
`;

const NodeContent = styled.div`
  width: 160px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60px;
  background: white;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  text-align: center;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &.current {
    border-color: #1890ff;
    background: #e6f7ff;
    font-weight: bold;
  }

  &.is-active-true {
    border-color: #52c41a;
    background: #f6ffed;
  }

  &.child {
    border-color: #fa541c;
    background: #fff2e8;
  }

  &.sibling {
    border-color: #722ed1;
    background: #f9f0ff;
  }
`;

const NodeTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: #262626;
`;

const NodeAmount = styled.div`
  color: #1890ff;
  font-weight: 500;
`;

const TransactionBox = styled.div`
  width: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  background: #fff7e6;
  border: 1px solid #ffd666;
  border-radius: 6px;
  font-size: 10px;
  color: #d48806;
  font-weight: 500;
`;

interface UtxoTreeFlowProps {
  commitment: CommitmentDetail;
  chainId: SupportedChainId;
  familyData?: Record<string, CommitmentDetail>;
  familyIds: {
    siblings1: Hash[];
    siblings2: Hash[];
    parents: Hash[];
    children: Hash[];
    all: {
      id: Hash;
      type: string;
    }[];
  };
}

// 自定义节点组件
const CustomNode = ({ data }: { data: { commitment: CommitmentDetail; node: CommitmentDetail; chainId: SupportedChainId } }) => {
  return (
    <NodeContent className={`is-active-${data.node.commitment === data.commitment.commitment ? 'true' : 'false'}`}>
      <Handle type="target" position={Position.Top} id="top" style={{ background: '#555' }} />
      <NodeTitle>{truncateAddress(data.node.commitment, 8, 8)}</NodeTitle>
      {data.node.amount && (
        <NodeAmount>
          <AssetAmount amount={data.node.amount} assetId={data.node.asset} chainId={data.chainId} /> <AssetSymbol assetId={data.node.asset} chainId={data.chainId} />
        </NodeAmount>
      )}
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ background: '#555' }} />
    </NodeContent>
  );
};

// 自定义交易节点组件
const TransactionNode = ({ data }: { data: any }) => {
  return (
    <TransactionBox>
      <Handle type="target" position={Position.Top} id="top" style={{ background: '#555' }} />
      {data.label}
      <Handle type="source" position={Position.Bottom} id="bottom" style={{ background: '#555' }} />
    </TransactionBox>
  );
};

const nodeTypes = {
  customNode: CustomNode,
  transactionNode: TransactionNode,
};

const UtxoTreeFlow: React.FC<UtxoTreeFlowProps> = ({ commitment, chainId, familyData, familyIds }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  useEffect(() => {
    const edges: Edge[] = [];
    const { siblings1, siblings2, parents, children } = familyIds;
    const view = { bx: 0, by: 0, dx: 200, dy: 100 };
    const nodesMap = new Map<string, Node>();
    parents.forEach((id, index) => {
      const data = familyData?.[id];
      if (!data) return;
      const nodeId = `${data.commitment}`;
      edges.push({ id: `parents-${nodeId}`, source: nodeId, target: 'created-tx', animated: true });
      if (nodesMap.has(nodeId)) return;
      nodesMap.set(nodeId, {
        id: nodeId,
        type: 'customNode',
        position: { x: view.bx + index * view.dx, y: view.by },
        data: { commitment, node: data, chainId },
      });
    });

    nodesMap.set('created-tx', { id: 'created-tx', type: 'transactionNode', position: { x: view.bx, y: view.by + 1 * view.dy }, data: { label: 'Created Transaction' } });
    let siblings1Count = 0;
    siblings1.forEach((id, index) => {
      const data = familyData?.[id];
      if (!data) return;
      const nodeId = `${data.commitment}`;
      edges.push({ id: `siblings1-${nodeId}`, source: 'created-tx', target: nodeId, animated: true });
      if (nodesMap.has(nodeId)) return;
      siblings1Count++;
      nodesMap.set(nodeId, {
        id: nodeId,
        type: 'customNode',
        position: { x: view.bx + index * view.dx, y: view.by + 2 * view.dy },
        data: { commitment, node: data, chainId },
      });
    });
    siblings2.forEach((id, index) => {
      const data = familyData?.[id];
      if (!data) return;
      const nodeId = `${data.commitment}`;
      edges.push({ id: `siblings2-${nodeId}`, source: nodeId, target: 'consumed-tx', animated: true });
      if (nodesMap.has(nodeId)) return;
      nodesMap.set(nodeId, {
        id: nodeId,
        type: 'customNode',
        position: { x: view.bx + (siblings1Count + index) * view.dx, y: view.by + 3 * view.dy },
        data: { commitment, node: data, chainId },
      });
    });
    if (children.length > 0) {
      nodesMap.set('consumed-tx', { id: 'consumed-tx', type: 'transactionNode', position: { x: view.bx, y: view.by + 4 * view.dy }, data: { label: 'Consumed Transaction' } });
    }
    children.forEach((id, index) => {
      const data = familyData?.[id];
      if (!data) return;
      const nodeId = `${data.commitment}`;
      edges.push({ id: `children-${nodeId}`, source: 'consumed-tx', target: nodeId, animated: true });
      if (nodesMap.has(nodeId)) return;
      nodesMap.set(nodeId, {
        id: nodeId,
        type: 'customNode',
        position: { x: view.bx + index * view.dx, y: view.by + 5 * view.dy },
        data: { commitment, node: data, chainId },
      });
    });
    console.log('edges', { edges, nodes });
    setNodes(Array.from(nodesMap.values()));
    setEdges(edges);
  }, [commitment, chainId, familyData, familyIds]);

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (node.type === 'customNode' && node.id !== commitment.commitment) {
        // 可以在这里添加点击节点的逻辑，比如导航到对应的UTXO详情页
        console.log('Clicked node:', node.id);
      }
    },
    [commitment.commitment],
  );

  return (
    <FlowContainer>
      <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} nodeTypes={nodeTypes} onNodeClick={onNodeClick} fitView fitViewOptions={{ padding: 0.2 }}>
        <Background />
        <Controls />
      </ReactFlow>
    </FlowContainer>
  );
};

export default UtxoTreeFlow;
